// Eskom PARCC — admin "View as" control (impersonation preview).
//
// Lets an admin preview the app exactly as another person sees it (their role +
// division + own allocations), to verify the role-based views. It is a READ-ONLY
// preview layered on the admin's real session — the token stays admin, so it can
// never escalate; it only ever narrows the view. Rendered only for admins (the
// Header gates on realRole), and only while NOT already previewing (the banner
// owns the exit). Sourced from the active access grants (admin-only query).
import { Eye } from 'lucide-react';
import { Select, type SelectItem } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { useAccessGrants } from '../../hooks';
import { IMPERSONATABLE_ROLES, ROLE_LABELS, impersonationGrant } from '../../lib/permissions';
import type { Role } from '../../lib/access';
import { normalizeEmail } from '../../lib/email';
import type { AccessGrantRecord } from '../../lib/db';

function personLabel(g: AccessGrantRecord): string {
  return (
    g.displayName?.trim() ||
    [g.firstName, g.lastName]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(' ') ||
    g.email
  );
}

export function ViewAsControl(): React.JSX.Element | null {
  const { realGrant, isImpersonating, impersonate } = useAuth();
  const grantsQuery = useAccessGrants();

  // The Header only mounts this for admins; bail while a preview is already active.
  if (isImpersonating) return null;

  const me = normalizeEmail(realGrant?.email);
  const people = (grantsQuery.data ?? [])
    .filter(
      (g) =>
        g.active && IMPERSONATABLE_ROLES.includes(g.role as Role) && normalizeEmail(g.email) !== me,
    )
    .sort((a, b) => a.role.localeCompare(b.role) || personLabel(a).localeCompare(personLabel(b)));
  if (people.length === 0) return null;

  // Group by role for the <optgroup>s, preserving the IMPERSONATABLE_ROLES order.
  const groups = new Map<Role, AccessGrantRecord[]>();
  for (const role of IMPERSONATABLE_ROLES) {
    const recs = people.filter((g) => g.role === role);
    if (recs.length) groups.set(role, recs);
  }

  function onPick(email: string): void {
    const rec = people.find((p) => p.email === email);
    if (!rec) return;
    const grant = impersonationGrant(rec);
    if (grant) impersonate(grant);
  }

  const items: SelectItem[] = [...groups.entries()].map(([role, recs]) => ({
    group: ROLE_LABELS[role],
    options: recs.map((g) => ({
      value: g.email,
      label: `${personLabel(g)}${g.division ? ` · ${g.division}` : ''}`,
    })),
  }));

  return (
    <label className="view-as" title="Preview the app as another role (read-only)">
      <Eye size={15} aria-hidden />
      {/* The designed, searchable dropdown in BOTH themes (alwaysDesigned): the
          grant list runs long, so a live search + the grouped, styled menu beats
          a native <select> here. */}
      <Select
        ariaLabel="View as another role"
        value=""
        placeholder="View as…"
        options={items}
        onChange={onPick}
        alwaysDesigned
        searchable
      />
    </label>
  );
}
